import React, { FC } from 'react';
import Container from 'FeatureModules/Setting/Components/Container';
import { CardContainer, ThemeButton, ThemeTypography } from 'DesignSystem';
import { styled, ButtonGroup, Box } from '@mui/material';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { Form, Formik } from 'formik';
import { siteDisplayOptionSchema } from 'FeatureModules/Setting/schemas/company.schema';
import { EFarmDisplayOption, ESiteDisplayOption } from 'FeatureModules/Auth/types/company.types';
import ItemSummary from 'DesignSystem/DataDisplay/ItemSummary/ItemSummary';
import { ESiteType, IFarmSite, ISite } from 'FeatureModules/Site/types/site.types';
import {
  getSiteSecondTitle,
  getSiteTitle,
  getSiteExtraInfo,
  SiteTypeLabels,
} from 'FeatureModules/Site/utils/site.utils';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import { useMutation } from '@apollo/client';
import { UPDATE_COMPANY_CONFIGS } from 'graphql/secure/mutations';
import SettingsHeader from '../Components/SettingsHeader';

interface IDataFormatSettingsValues {
  farmDisplayOption: EFarmDisplayOption;
  siteDisplayOption: ESiteDisplayOption;
}

const FARM: Omit<
  IFarmSite,
  | 'id'
  | 'logs'
  | 'createdBy'
  | 'locationCoordinates'
  | 'modifiedTimestamp'
  | 'createdTimestamp'
  | 'inUse'
  | 'coordinateFeatures'
  | 'countryCode'
  | 'plotsCount'
  | 'plotsSizeHa'
> = {
  title: 'Sunny fields farm',
  externalId: '169283c-e4d4',
  lang: 'en',
  siteType: ESiteType.FARM,
  farmOwner: 'José Gonzales',
  locationName: 'Some place, Some region, Country',
  rawMaterial: { id: 'cocoa', title: 'Cocoa' },
  sizeHa: 58,
  ownedBy: {
    id: 'owner-id',
    name: 'Qualcoco',
    modifiedTimestamp: new Date(),
    createdTimestamp: new Date(),
    status: 'INVITED',
    countriesOfBusiness: [],
  },
};

const SITE: Omit<
  ISite,
  | 'id'
  | 'logs'
  | 'createdBy'
  | 'locationCoordinates'
  | 'modifiedTimestamp'
  | 'createdTimestamp'
  | 'inUse'
  | 'coordinateFeatures'
  | 'countryCode'
> = {
  title: 'Cocoa Derivatives Facility A',
  externalId: '8B567hg-k9f5',
  lang: 'en',
  siteType: ESiteType.FACTORY,
  locationName: 'Some place, Some region, Country',
  ownedBy: {
    id: 'owner-id',
    name: 'Qualcoco',
    modifiedTimestamp: new Date(),
    createdTimestamp: new Date(),
    status: 'INVITED',
    countriesOfBusiness: [],
  },
};

const OptionsContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  borderRadius: 8,
  padding: theme.spacing(3),
  background: theme.custom.themeColors.grayScale[5],
}));

const GrayTitle = styled('span')(({ theme }) => ({
  color: theme.custom.themeColors.grayScale[60],
}));

const DataFormatSettings: FC = () => {
  const { company } = useCompanyPlanContext();
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const { logEvent } = useLogEvent();

  const [updateCompanyConfigs] = useMutation(UPDATE_COMPANY_CONFIGS, {
    onCompleted: () => {
      setSuccessMessage('Success! Data format settings have been updated.');
      logEvent('WELCOME_SLIDES_UPDATE_DESIGN');
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your data format settings.');
    },
  });

  const initialValues: IDataFormatSettingsValues = {
    ...siteDisplayOptionSchema.default(),
    farmDisplayOption: company?.farmDisplayOption,
    siteDisplayOption: company?.siteDisplayOption,
  };

  const handleSubmit = (values: IDataFormatSettingsValues) => {
    return updateCompanyConfigs({
      variables: {
        input: {
          farmDisplayOption: values.farmDisplayOption,
          siteDisplayOption: values.siteDisplayOption,
        },
      },
    });
  };

  return (
    <Container>
      <SettingsHeader title="Site title" />
      <ThemeTypography variant="BODY_MEDIUM">
        Decide what is displayed as the main Site title (in black bold text style) across the
        platform. You can either set the ID or the Name as the site title by toggling below. You can
        change settings for Farms and other Site types separately. The default is to show the ID as
        the title.
      </ThemeTypography>
      <Formik<IDataFormatSettingsValues>
        initialValues={initialValues}
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={siteDisplayOptionSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, submitForm, setFieldValue, values }) => (
          <Form>
            <OptionsContainer>
              <ThemeTypography variant="BODY_MEDIUM_BOLD">
                Site type: <GrayTitle>Farm</GrayTitle>
              </ThemeTypography>
              <Box mt={0.5} />
              <ButtonGroup>
                <ThemeButton
                  data-testid="farm-id-button"
                  className={
                    values.farmDisplayOption === EFarmDisplayOption.FARM_ID ? 'selected' : ''
                  }
                  onClick={() => setFieldValue('farmDisplayOption', EFarmDisplayOption.FARM_ID)}
                  typographyColor="ALMOST_BLACK"
                >
                  Farm ID
                </ThemeButton>
                <ThemeButton
                  data-testid="farm-name-button"
                  className={
                    values.farmDisplayOption === EFarmDisplayOption.FARM_NAME ? 'selected' : ''
                  }
                  onClick={() => setFieldValue('farmDisplayOption', EFarmDisplayOption.FARM_NAME)}
                  typographyColor="ALMOST_BLACK"
                >
                  Farm name
                </ThemeButton>
                <ThemeButton
                  data-testid="farm-owner-name-button"
                  className={
                    values.farmDisplayOption === EFarmDisplayOption.FARM_OWNER_NAME
                      ? 'selected'
                      : ''
                  }
                  onClick={() =>
                    setFieldValue('farmDisplayOption', EFarmDisplayOption.FARM_OWNER_NAME)
                  }
                  typographyColor="ALMOST_BLACK"
                >
                  Farm owner name
                </ThemeButton>
              </ButtonGroup>
              <Box mt={2}>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">Preview example:</ThemeTypography>
                <Box mt={0.5} />
                <CardContainer padding={1.5}>
                  <ItemSummary
                    imagePlaceholder="site-placeholder"
                    title={getSiteTitle({
                      site: FARM,
                      farmDisplayOption: values.farmDisplayOption,
                      siteDisplayOption: values.siteDisplayOption,
                    })}
                    externalId={getSiteSecondTitle({
                      site: FARM,
                      farmDisplayOption: values.farmDisplayOption,
                      siteDisplayOption: values.siteDisplayOption,
                    })}
                    extraInfo={getSiteExtraInfo({
                      site: FARM,
                      farmDisplayOption: values.farmDisplayOption,
                      siteDisplayOption: values.siteDisplayOption,
                    })}
                    details={[
                      {
                        icon: 'tag-filled',
                        title: SiteTypeLabels[FARM.siteType],
                      },
                      {
                        icon: 'raw-material',
                        title: FARM.rawMaterial.title,
                      },
                      {
                        icon: 'farm',
                        title: `${FARM.sizeHa} ha`,
                      },
                      {
                        icon: 'location',
                        title: FARM.locationName,
                      },
                      { icon: 'partner', title: FARM.ownedBy?.name || '' },
                    ]}
                  />
                </CardContainer>
              </Box>
            </OptionsContainer>
            <OptionsContainer>
              <ThemeTypography variant="BODY_MEDIUM_BOLD">
                Other site type <GrayTitle>(e.g. Factory, Warehouse)</GrayTitle>
              </ThemeTypography>
              <Box mt={0.5} />
              <ButtonGroup>
                <ThemeButton
                  data-testid="site-id-button"
                  className={
                    values.siteDisplayOption === ESiteDisplayOption.SITE_ID ? 'selected' : ''
                  }
                  onClick={() => setFieldValue('siteDisplayOption', ESiteDisplayOption.SITE_ID)}
                  typographyColor="ALMOST_BLACK"
                >
                  Site ID
                </ThemeButton>
                <ThemeButton
                  data-testid="site-name-button"
                  className={
                    values.siteDisplayOption === ESiteDisplayOption.SITE_NAME ? 'selected' : ''
                  }
                  onClick={() => setFieldValue('siteDisplayOption', ESiteDisplayOption.SITE_NAME)}
                  typographyColor="ALMOST_BLACK"
                >
                  Site name
                </ThemeButton>
              </ButtonGroup>
              <Box mt={2}>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">Preview example:</ThemeTypography>
                <Box mt={0.5} />
                <CardContainer padding={1.5}>
                  <ItemSummary
                    imagePlaceholder="site-placeholder"
                    title={getSiteTitle({
                      site: SITE,
                      farmDisplayOption: values.farmDisplayOption,
                      siteDisplayOption: values.siteDisplayOption,
                    })}
                    externalId={getSiteSecondTitle({
                      site: SITE,
                      farmDisplayOption: values.farmDisplayOption,
                      siteDisplayOption: values.siteDisplayOption,
                    })}
                    details={[
                      {
                        icon: 'tag-filled',
                        title: SiteTypeLabels[SITE.siteType],
                      },
                      {
                        icon: 'location',
                        title: SITE.locationName,
                      },
                      { icon: 'partner', title: SITE.ownedBy?.name || '' },
                    ]}
                  />
                </CardContainer>
              </Box>
            </OptionsContainer>
            <Box mt={4} display="flex" justifyContent="flex-end">
              <ThemeButton
                variant="contained"
                color="PRIMARY"
                size="large"
                onClick={() => submitForm()}
                disabled={isSubmitting}
                loading={isSubmitting}
                data-testid="save-data-format-settings-btn"
              >
                Save
              </ThemeButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default DataFormatSettings;
